import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Box, Typography, Button, Stack, Paper, Snackbar, Alert, CircularProgress, IconButton } from '@mui/material';
import type { Page } from '../../models';
import {
  DndContext,
  useDraggable,
  useDroppable,
  DragOverlay,
  type DragEndEvent,
  type DragStartEvent,
  type DragOverEvent,
  MeasuringStrategy,
  closestCenter,
} from '@dnd-kit/core';
import { createPortal } from 'react-dom';
import { pageApi } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { KeyboardArrowRight, KeyboardArrowDown, DragIndicator, Folder } from '@mui/icons-material';

// A type for our flattened tree structure
type FlattenedItem = {
  id: string;
  parentId: string | null;
  depth: number;
  index: number;
} & Page;

// Props interface for the PageHierarchy component
interface PageHierarchyProps {
  pages?: Page[];
  onNavigate?: (path: string) => void;
  fullPageMode?: boolean;
}

// --- Re-engineered Tree Logic ---

// Flattens the tree for dnd-kit and adds depth/parent information
function flattenTree(
  items: any[],
  parentId: string | null = null,
  depth = 0
): FlattenedItem[] {
  return items.reduce((acc, item, index) => {
    return [
      ...acc,
      { ...item, parentId, depth, index },
      ...flattenTree(item.children || [], String(item.id), depth + 1),
    ];
  }, []);
}

// Builds the tree structure from a flat list of pages
function buildPageTree(pages: Page[]) {
  const idToNodeMap: Record<string, any> = {};
  const roots: any[] = [];
  pages.forEach(page => {
    idToNodeMap[String(page.id)] = { ...page, children: [] };
  });
  pages.forEach(page => {
    if (page.parent && idToNodeMap[String(page.parent)]) {
      idToNodeMap[String(page.parent)].children.push(idToNodeMap[String(page.id)]);
    } else {
      roots.push(idToNodeMap[String(page.id)]);
    }
  });
  return roots;
}

// --- The Main Component ---

const PageHierarchy: React.FC<PageHierarchyProps> = ({ pages = [], onNavigate, fullPageMode }) => {
  const [treePages, setTreePages] = useState<Page[]>(pages);
  const [dirty, setDirty] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);

  // *** NEW: State to track the drop indicator's position ***
  const [dropIndicator, setDropIndicator] = useState<{
    parentId: string | null;
    index: number;
  } | null>(null);
  
  // State for controlled expansion
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(fullPageMode);
  const navigate = useNavigate();
  const overIdTimeout = useRef<number | null>(null);

  // Fetch pages if in fullPageMode
  useEffect(() => {
    if (fullPageMode) {
      setLoading(true);
      pageApi.list({ page_size: 1000, is_draft: false })
        .then(res => {
          const fetchedPages = res.data.results || res.data;
          setTreePages(fetchedPages);
          // Auto-expand all parents on initial load
          const parentIds = [...new Set(fetchedPages.map(p => p.parent).filter(Boolean))];
          setExpandedItems(parentIds.map(String));
        })
        .finally(() => setLoading(false));
    }
  }, [fullPageMode]);
  
  // Memoize flattened and tree structures for performance
  const pageTree = useMemo(() => buildPageTree(treePages), [treePages]);
  const flattenedTree = useMemo(() => flattenTree(pageTree), [pageTree]);
  const activeItem = useMemo(() => activeId ? flattenedTree.find(item => String(item.id) === activeId) : null, [activeId, flattenedTree]);

  // --- Drag and Drop Handlers ---

  const handleDragStart = ({ active }: DragStartEvent) => {
    setActiveId(String(active.id));
    setDropIndicator(null); // Reset indicator on new drag
  };

  const handleDragOver = ({ over, active }: DragOverEvent) => {
    const overId = over?.id ? String(over.id) : null;
    if (!overId || !active) return;
    
    // Handle root element drop
    if (overId === 'root') {
      setDropIndicator({
        parentId: null,
        index: 0
      });
      return;
    }
    
    const overItem = flattenedTree.find(item => String(item.id) === overId);
    if (!overItem) return;

    // Auto-expand on hover
    if (overIdTimeout.current) window.clearTimeout(overIdTimeout.current);
    overIdTimeout.current = window.setTimeout(() => {
      const hasChildren = flattenedTree.some(item => item.parentId === overId);
      if (hasChildren && !expandedItems.includes(overId)) {
        setExpandedItems(prev => [...prev, overId]);
      }
    }, 500);

    // *** NEW: Logic for positioning the drop indicator ***
    const isBelow = over && active.id !== over.id && active.rect.current.translated && 
                    over.rect.top + over.rect.height / 2 < active.rect.current.translated.top + active.rect.current.translated.height / 2;

    setDropIndicator({
        parentId: isBelow ? overItem.parentId : overItem.id, // Drop into item or alongside it
        index: isBelow ? (overItem.index || 0) + 1 : 0 // If below, insert after, otherwise at the start of children
    });
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    const overId = over?.id ? String(over.id) : null;

    if (!overId || String(active.id) === overId || !dropIndicator) {
      setActiveId(null);
      setDropIndicator(null);
      return;
    }
    
    const activeItem = flattenedTree.find(item => String(item.id) === String(active.id));
    if (!activeItem) return;

    // Handle dropping on root element
    if (overId === 'root') {
      setTreePages(prev =>
        prev.map(p =>
          String(p.id) === String(active.id) ? { ...p, parent: null } : p
        )
      );
      setDirty(true);
      setActiveId(null);
      setDropIndicator(null);
      return;
    }

    // Prevent dropping into a descendant
    let tempItem = flattenedTree.find(i => String(i.id) === dropIndicator.parentId);
    while (tempItem) {
        if (String(tempItem.id) === String(active.id)) {
            setSnackbar({ open: true, message: 'Cannot move a page into its own child.', severity: 'error' });
            setActiveId(null);
            setDropIndicator(null);
            return;
        }
        tempItem = flattenedTree.find(i => String(i.id) === tempItem?.parentId);
    }
    
    const newParentId = dropIndicator.parentId ? Number(dropIndicator.parentId) : null;

    setTreePages(prev =>
      prev.map(p =>
        String(p.id) === String(active.id) ? { ...p, parent: newParentId } : p
      )
    );
    
    // Note: Reordering logic would be more complex, involving updating an 'order' field.
    // This implementation primarily focuses on reparenting, which is simpler.
    
    if (newParentId && !expandedItems.includes(String(newParentId))) {
      setExpandedItems(prev => [...prev, String(newParentId)]);
    }

    setDirty(true);
    setActiveId(null);
    setDropIndicator(null);
  };
  
   const handleDragCancel = () => {
    setActiveId(null);
    setDropIndicator(null);
  };

  // --- API and Navigation ---

  const handleSave = async () => {
    try {
      await pageApi.bulkUpdateHierarchy(
        treePages.map(p => ({ id: p.id, parent: p.parent ?? null }))
      );
      setSnackbar({ open: true, message: 'Hierarchy saved successfully', severity: 'success' });
      setDirty(false);
      if (fullPageMode) {
        setTimeout(() => navigate('/cms/pages'), 1000);
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to save hierarchy', severity: 'error' });
    }
  };

  const DropIndicator = ({ depth }: { depth: number }) => (
    <Box sx={{
        height: '4px',
        width: `calc(100% - ${depth * 24 + 16}px)`, // Adjust width based on depth
        ml: `${depth * 24 + 16}px`, // Align with item content
        backgroundColor: 'primary.main',
        borderRadius: '2px',
        position: 'absolute',
        zIndex: 10,
    }} />
  );

  // --- Root Element Component ---
  const RootElement = React.memo(() => {
    const { setNodeRef: setDroppableNodeRef, isOver } = useDroppable({
      id: 'root',
    });

    const rootPages = pageTree.filter(item => !item.parent);

    return (
      <Box ref={setDroppableNodeRef}>
        {/* Visual Drop Indicator for root */}
        {isOver && dropIndicator && dropIndicator.parentId === null && (
          <Box sx={{
            height: '4px',
            width: 'calc(100% - 16px)',
            ml: '16px',
            backgroundColor: 'primary.main',
            borderRadius: '2px',
            position: 'absolute',
            zIndex: 10,
          }} />
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            paddingY: 1,
            paddingX: 1,
            borderRadius: 1,
            backgroundColor: isOver ? 'action.hover' : 'grey.50',
            border: isOver ? '2px dashed' : '1px solid',
            borderColor: isOver ? 'primary.main' : 'grey.300',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          {/* Root Icon */}
          <Folder fontSize="small" color="primary" sx={{ mr: 1 }} />
          
          {/* Root Content */}
          <Typography
            variant="h6"
            sx={{ 
              flexGrow: 1, 
              userSelect: 'none',
              color: 'primary.main',
              fontWeight: 'bold'
            }}
          >
            / ({rootPages.length})
          </Typography>
        </Box>
        
        {/* Root Pages - Always visible */}
        {rootPages.length > 0 && (
          <Box sx={{ ml: 2, mt: 1 }}>
            {rootPages.map((item) => (
              <TreeItemComponent key={item.id} item={item} depth={1} />
            ))}
          </Box>
        )}
      </Box>
    );
  });

  // --- Custom Tree Item for D&D ---

  const TreeItemComponent = React.memo(({ item, depth = 0 }: { item: any; depth?: number }) => {
    const {
      attributes,
      listeners,
      setNodeRef: setDraggableNodeRef,
      transform,
      isDragging,
    } = useDraggable({
      id: String(item.id),
      data: { item } // Pass item data for the overlay
    });
    const { setNodeRef: setDroppableNodeRef, isOver } = useDroppable({
      id: String(item.id),
    });

    const isExpanded = expandedItems.includes(String(item.id));
    const hasChildren = item.children && item.children.length > 0;
    
    // *** NEW: Check if the drop indicator should be shown for this item ***
    const showDropIndicatorAsChild = dropIndicator && dropIndicator.parentId === String(item.id);

    const style = {
      opacity: isDragging ? 0.4 : 1,
      transform: transform ? `translate3d(0, ${transform.y}px, 0)` : undefined, // Constrain transform to Y axis
      position: 'relative',
    };

    return (
      <Box ref={setDroppableNodeRef} sx={style as any}>
        {/* Visual Drop Indicator */}
        {isOver && showDropIndicatorAsChild && <DropIndicator depth={depth + 1} />}

        <Box
          ref={setDraggableNodeRef}
          sx={{
            display: 'flex',
            alignItems: 'center',
            paddingLeft: `${depth * 24}px`,
            paddingY: 0.5,
            borderRadius: 1,
            backgroundColor: isOver ? 'action.hover' : 'transparent',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          {/* Drag Handle */}
          <Box {...listeners} {...attributes} sx={{ cursor: 'grab', display: 'flex', alignItems: 'center', p: 1, '&:active': { cursor: 'grabbing'} }}>
            <DragIndicator fontSize="small" color="disabled" />
          </Box>
          
          {/* Toggle Button */}
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setExpandedItems(prev => 
                isExpanded 
                  ? prev.filter(id => id !== String(item.id))
                  : [...prev, String(item.id)]
              );
            }}
            sx={{ visibility: hasChildren ? 'visible' : 'hidden' }}
          >
            {isExpanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
          </IconButton>
          
          {/* Item Content */}
          <Typography
            variant="body1"
            onClick={() => onNavigate?.(`/cms/pages/${item.id}/view`)}
            sx={{ flexGrow: 1, cursor: 'pointer', userSelect: 'none', p: 1 }}
          >
            {item.title}
          </Typography>
        </Box>
        {hasChildren && isExpanded && (
          <Box>
            {item.children.map((child: any) => (
              <TreeItemComponent key={child.id} item={child} depth={depth + 1} />
            ))}
          </Box>
        )}
      </Box>
    );
  });

  // --- Render Logic ---

  const content = (
    <Box>
       <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="h6">Page Hierarchy</Typography>
        <Button variant="contained" color="primary" onClick={handleSave} disabled={!dirty}>
          Save Hierarchy
        </Button>
      </Stack>
      <Paper variant="outlined" sx={{ p: 1, mb: 2, maxHeight: '70vh', overflowY: 'auto' }}>
         {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DndContext
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragOver={handleDragOver}
            onDragCancel={handleDragCancel}
            collisionDetection={closestCenter} // `closestCenter` is better for this type of indicator logic
            measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
          >
            <Box sx={{ flexGrow: 1 }}>
              <RootElement />
            </Box>
            {createPortal(
              // *** UPDATED DragOverlay for better UX ***
              <DragOverlay dropAnimation={null}>
                {activeId && activeItem ? (
                  <Paper elevation={3} sx={{
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}>
                     <DragIndicator fontSize="small" color="disabled" />
                     <Typography variant="body1">{activeItem.title}</Typography>
                  </Paper>
                ) : null}
              </DragOverlay>,
              document.body,
            )}
          </DndContext>
        )}
      </Paper>
      <Typography variant="body2" color="text.secondary">
        Drag and drop pages to reorganize the hierarchy. Drop on the "Root Pages" folder to remove a page's parent. Click "Save Hierarchy" to persist changes.
      </Typography>
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar(s => ({...s, open: false}))}>
        <Alert onClose={() => setSnackbar(s => ({...s, open: false}))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );

  return content;
};

export default PageHierarchy;